import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["canvas"]
  static values = {
    slug: String,
    diagram: Object,
    generated: Boolean
  }

  connect() {
    this.renderReactFlow()
    // Delay setup to ensure DOM is ready
    requestAnimationFrame(() => this.setupDragAndDrop())
  }

  disconnect() {
    if (this.root) {
      this.root.unmount()
    }
    this.cleanupDragAndDrop()
  }

  get paletteElement() {
    // Find palette in sidebar (outside controller scope)
    return document.getElementById("flow-sidebar")?.querySelector("[data-node-palette]")
  }

  setupDragAndDrop() {
    const palette = this.paletteElement
    if (palette) {
      const items = palette.querySelectorAll("[data-node-type]")
      this.boundDragStart = this.handleDragStart.bind(this)
      this.boundDragEnd = this.handleDragEnd.bind(this)
      items.forEach(item => {
        item.addEventListener("dragstart", this.boundDragStart)
        item.addEventListener("dragend", this.boundDragEnd)
      })
    }

    this.boundDragOver = this.handleDragOver.bind(this)
    this.boundDrop = this.handleDrop.bind(this)
    this.canvasTarget.addEventListener("dragover", this.boundDragOver)
    this.canvasTarget.addEventListener("drop", this.boundDrop)
  }

  cleanupDragAndDrop() {
    const palette = this.paletteElement
    if (palette && this.boundDragStart) {
      const items = palette.querySelectorAll("[data-node-type]")
      items.forEach(item => {
        item.removeEventListener("dragstart", this.boundDragStart)
        item.removeEventListener("dragend", this.boundDragEnd)
      })
    }
    if (this.boundDragOver) {
      this.canvasTarget?.removeEventListener("dragover", this.boundDragOver)
      this.canvasTarget?.removeEventListener("drop", this.boundDrop)
    }
  }

  handleDragStart(event) {
    const nodeType = event.target.closest("[data-node-type]")?.dataset.nodeType
    if (nodeType) {
      event.dataTransfer.setData("application/reactflow", nodeType)
      event.dataTransfer.effectAllowed = "move"
      event.target.closest(".node-palette-item")?.classList.add("dragging")
    }
  }

  handleDragEnd(event) {
    event.target.closest(".node-palette-item")?.classList.remove("dragging")
  }

  handleDragOver(event) {
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"
  }

  handleDrop(event) {
    event.preventDefault()
    const nodeType = event.dataTransfer.getData("application/reactflow")
    if (!nodeType) return

    // Get drop position relative to canvas
    const rect = this.canvasTarget.getBoundingClientRect()
    const position = {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    }

    // Dispatch custom event for React to handle
    const customEvent = new CustomEvent("flow:addNode", {
      detail: { type: nodeType, position },
      bubbles: true
    })
    this.canvasTarget.dispatchEvent(customEvent)
  }

  save() {
    const customEvent = new CustomEvent("flow:save", { bubbles: true })
    this.canvasTarget.dispatchEvent(customEvent)
  }

  export() {
    // Dispatch event for React to handle export
    const customEvent = new CustomEvent("flow:export", { bubbles: true })
    this.canvasTarget.dispatchEvent(customEvent)
  }

  renderReactFlow() {
    const FlowCanvas = window.FlowCanvas
    const React = window.React
    const createRoot = window.createRoot

    if (!FlowCanvas || !React || !createRoot) {
      console.error("React components not loaded")
      return
    }

    const diagram = this.diagramValue || { nodes: [], edges: [] }

    this.root = createRoot(this.canvasTarget)
    this.root.render(
      React.createElement(FlowCanvas, {
        flowSlug: this.slugValue,
        initialNodes: diagram.nodes || [],
        initialEdges: diagram.edges || [],
        initialViewport: diagram.viewport,
        isGenerated: this.generatedValue
      })
    )
  }
}
